import { useMemo } from 'react';
import { useClubTheme } from '../../../contexts/ClubThemeContext';

// ----------------------------------------------------------------------------
//  Instat scouting — iOS-inspired design tokens (light mode)
//  No MUI ThemeProvider is injected: the rest of the app runs on the default
//  MUI light theme. Instat components opt in via `useInstatDesign()` and apply
//  tokens through `sx={}` / inline styles. Accent color comes from the club.
// ----------------------------------------------------------------------------

export const INSTAT_COLORS = {
  // Backdrop — Apple "Cloud" gray
  canvas: '#F5F5F7',
  canvasAlt: '#FAFAFC',

  // Cards & floating surfaces
  surface: '#FFFFFF',
  surfaceAlt: '#F9F9FB',
  surfaceOverlay: 'rgba(255,255,255,0.72)', // for glass headers

  // Text hierarchy (Apple HIG)
  text: '#1D1D1F',
  textSecondary: '#86868B',
  textTertiary: '#AEAEB2',
  textInverse: '#FFFFFF',

  // Dividers — translucent warm gray, not hard lines
  divider: 'rgba(60,60,67,0.08)',
  dividerStrong: 'rgba(60,60,67,0.14)',

  // iOS system semantics
  success: '#34C759',
  successSoft: 'rgba(52,199,89,0.12)',
  danger: '#FF3B30',
  dangerSoft: 'rgba(255,59,48,0.12)',
  warning: '#FF9500',
  warningSoft: 'rgba(255,149,0,0.12)',
  info: '#007AFF',
  infoSoft: 'rgba(0,122,255,0.12)',

  // Heatmap scale (cold → hot), used on career %
  heatmap: ['#EEF5FF', '#D6E8FF', '#A9CEFF', '#60A5FA', '#007AFF', '#34C759'],

  // Basketball-y court tint (re-used by shot chart)
  court: '#EDE1BE',
};

export const INSTAT_RADIUS = {
  xs: 6,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  xxl: 28,
  pill: 999,
};

export const INSTAT_SHADOWS = {
  // Very soft, multi-layer — "floating paper" on Cloud bg
  card: '0 1px 2px rgba(17,24,39,0.04), 0 8px 24px rgba(17,24,39,0.05)',
  cardHover: '0 1px 2px rgba(17,24,39,0.05), 0 14px 34px rgba(17,24,39,0.08)',
  cardStrong: '0 1px 2px rgba(17,24,39,0.06), 0 22px 48px rgba(17,24,39,0.10)',
  inner: 'inset 0 1px 0 rgba(255,255,255,0.6)',
  // For elevated sticky bars with backdrop-filter
  glass: '0 1px 0 rgba(60,60,67,0.08), 0 4px 24px rgba(17,24,39,0.06)',
};

export const INSTAT_MOTION = {
  // Apple "standard" easing
  fast: '160ms cubic-bezier(0.25, 0.1, 0.25, 1)',
  base: '240ms cubic-bezier(0.25, 0.1, 0.25, 1)',
  slow: '360ms cubic-bezier(0.25, 0.1, 0.25, 1)',
  // Spring — for hover lifts, segment transitions
  spring: '420ms cubic-bezier(0.34, 1.56, 0.64, 1)',
};

export const INSTAT_TYPO = {
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Inter", "Segoe UI", Roboto, sans-serif',
  fontFeatureSettings: '"ss01", "cv11"',
  tabular: { fontVariantNumeric: 'tabular-nums' },

  display: { fontSize: 40, fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.05 },
  displaySmall: { fontSize: 32, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.1 },
  title: { fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.25 },
  headline: { fontSize: 17, fontWeight: 600, letterSpacing: '-0.005em' },
  body: { fontSize: 15, fontWeight: 400, letterSpacing: 0 },
  subhead: { fontSize: 13, fontWeight: 500 },
  caption: { fontSize: 12, fontWeight: 500, color: INSTAT_COLORS.textSecondary },
  micro: { fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' },
};

// Convert "#RRGGBB" → "rgba(r,g,b,a)"
export function withAlpha(hex, alpha) {
  if (!hex || !hex.startsWith('#')) return hex;
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Map a 0..1 value to a color on the heatmap scale
export function heatmapColor(t) {
  if (t == null || Number.isNaN(t)) return INSTAT_COLORS.surfaceAlt;
  const clamped = Math.max(0, Math.min(1, t));
  const scale = INSTAT_COLORS.heatmap;
  const i = clamped * (scale.length - 1);
  const lo = Math.floor(i);
  const hi = Math.ceil(i);
  return scale[clamped < 0.5 ? lo : hi];
}

/**
 * Main hook — merges static tokens with live club accent.
 */
export function useInstatDesign() {
  const club = useClubTheme();

  return useMemo(() => {
    const accent = club?.primaryColor || '#007AFF';
    const accentSecondary = club?.secondaryColor || accent;
    return {
      colors: INSTAT_COLORS,
      radius: INSTAT_RADIUS,
      shadows: INSTAT_SHADOWS,
      motion: INSTAT_MOTION,
      typo: INSTAT_TYPO,

      accent,
      accentSecondary,
      accentSoft: withAlpha(accent, 0.12),
      accentSofter: withAlpha(accent, 0.06),
      accentBorder: withAlpha(accent, 0.28),
      accentStrong: withAlpha(accent, 0.9),
      accentGradient: `linear-gradient(135deg, ${accent} 0%, ${accentSecondary} 100%)`,

      clubName: club?.clubName,
      clubLogo: club?.logo,

      withAlpha,
      heatmapColor,
    };
  }, [club?.primaryColor, club?.secondaryColor, club?.clubName, club?.logo]);
}
